import {IWedEditor, OWedManagerConfig} from "back/edit/wed/wedEditor";
import {OWedEditorBoxConfig} from "back/edit/wed/wedEditorBox";
import * as generators from "back/wsp/views/gen/genActions";
import * as actions from "lib/commons/actions";
import {IAction} from "lib/commons/actions";
import * as htmlLit from "lib/commons/utils/htmlLit";
import {TemplateResult, xhtml} from "lib/commons/utils/htmlLit";
import {OItemImgInit} from "back/wsp/views/item/itemImg";
import {OItemVideoInit} from "back/wsp/views/item/itemVideo";
import {OItemXmlEdInit} from "back/wsp/views/item/itemXmlEd";
import {IItemUiEnv, ISrcMainCtx, SrcMain} from "back/wsp/views/itemMain";
import {Area} from "lib/commons/areas";
import {EndPointResolver, IBody, IEndPoint, IO, IPathResolver} from "lib/commons/io/io";
import {JLastDatas} from "lib/commons/lastDatas";
import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {ISecurityCtx} from "lib/commons/security";
import {DOM} from "lib/commons/xml/dom";
import {JML} from "lib/commons/xml/jml";
import {Schema} from "lib/edit/schema/schema";
import {ChainUniverse, IChainEnv} from "lib/wsp/chain";
import {EItResp, EItStatus, IShortDescCtx, ITEM, JGenInfo} from "lib/wsp/item";
import {ESrcSt, JSrcFields, SRC, srcUri} from "lib/wsp/src";
import {IWspEnv, IWspUiEnv, JWspType, JWspTypeInst, WSP, Wsp, WspSrv, wspUri} from "lib/wsp/wsp";
import "back/wsp/widgets/remoteView";
import "back/wsp/widgets/codeView";
import {OItemDynGenInit} from "back/wsp/views/item/itemDynGen";
import {OItemIframeInit} from "back/wsp/views/item/itemIframe";
import {OItemAudioInit} from "back/wsp/views/item/itemAudio";
import {OTaskDynGenInit} from "back/wsp/views/task/taskDynGen";
import {OTaskLayoutInit} from "back/wsp/views/task/taskLayout";
import {OTaskFieldInit, OTaskXmlFieldInit} from "back/wsp/views/task/taskFields";
import {OTaskCtDynGenInit, OTaskCtEditInit} from "back/wsp/views/task/taskContent";
import {CidProcess} from "lib/core/cid";
import {ExportRes, ImportFolder, ImportRes} from "back/wsp/actions/itemActions";
import {OItemRemoteInit} from "back/wsp/views/item/itemRemote";
import {ICidInfos} from "back/core/widgets/cidUi";
import {EInvolvement, ETaskStage, JTaskStates} from "lib/wsp/lcTask";
import {SkMetaLib} from "lib/edit/schema/schemaMeta";
import {RelaxNgSchemaBuilder} from "lib/edit/schema/schemaBuilder";
import {DynGen} from "back/wsp/views/dynGen";
import {OItemTxtInit} from "back/wsp/views/item/itemTxt";
import {OFreeElementAsyncInit, OFreeElementInit} from "back/commons/basis";
import {OItemCodeEdInit} from "back/wsp/views/item/itemCodeEd";
import {helpId, IHelpDb} from "back/help/helpApi";
import {OHelpViewsInit} from "back/help/views/helpViews";
import {getHelpLocalDb, HelpLocalDb} from "lib/help/helpLocalDb";
import {IFolder} from "lib/commons/io/files";

/**
 * Définit un modèle documentaire du point de vue Wui. Il combine un wspType et 0 à N wspOptions.
 * Le WspMetaUi :
 * - spécifie un sous-reg de IReg<IChainEnv>,
 * - fournit les ItemType du modèle. Chaque itemType possède son SubReg, fils de WspMetaUi.reg.
 *
 * A l'init d'un Wsp, le WspMetaUi.reg doit être copié dans Wsp.reg.
 */
export class WspMetaUi implements ILcProvider {

	/** Construit un WspMetaUi imbriquant un wspType et des wspOptions dans le contexte d'un ChainUniverse. */
	static async createWspMetaUi(wspServer: WspSrv, wspMeta: JWspTypeInst): Promise<WspMetaUi> {
		const wspTypeEP = WspMetaUi.buildWspTypeUiEndPoint(wspServer, wspMeta.wspType);
		const wspOptionEPs = wspMeta.wspOptions ? wspMeta.wspOptions.map((opt) => WspMetaUi.buildWspTypeUiEndPoint(wspServer, opt, wspTypeEP)) : null;
		const wspMetaUi = new WspMetaUi(wspServer.reg, wspTypeEP, wspOptionEPs);
		//Chargement des modules
		let postConfigs: postConfigWspMeta[];
		wspMetaUi._currrentWspTypeEP = wspTypeEP;
		const next = await (await wspTypeEP.importJs("wspType.js") as IWspTypeModule).configWspMeta(wspMetaUi, wspTypeEP, UTILS);
		if (next) postConfigs = [next];
		if (wspOptionEPs) for (const wpsOptUrl of wspOptionEPs) {
			wspMetaUi._currrentWspTypeEP = wpsOptUrl;
			const next = await (await wpsOptUrl.importJs("wspOption.js") as IWspTypeModule).configWspMeta(wspMetaUi, wpsOptUrl, UTILS);
			if (next) (postConfigs || (postConfigs = [])).push(next);
		}
		wspMetaUi._currrentWspTypeEP = null;
		while (postConfigs?.length > 0) {
			let newPostConfigs: postConfigWspMeta[];
			for (const config of postConfigs) {
				const next = await config(wspMetaUi, UTILS);
				if (next) (newPostConfigs || (newPostConfigs = [])).push(next);
			}
			postConfigs = newPostConfigs;
		}

		//Surcharge par le portlet via un svc overrideWspMetaUi.
		//Note: ce svc overrideWspMetaUi peut lui-même être surchargé dans le wspMetaUi.reg pour contrer une surcharge du portlet.
		const override = wspMetaUi.reg.getSvc("overrideWspMetaUi") as (wspServer: WspSrv, wspMetaUi: WspMetaUi, UTILS: {}) => Promise<void>;
		if (override) await override(wspServer, wspMetaUi, UTILS);

		wspMetaUi.hasResps = hasResps(wspMetaUi._dataItemTypes);
		if (wspMetaUi.hasResps) {
			//Ajout des champs longDesc/shortDesc
			wspMetaUi.reg.addToList('srcFields:shortDesc', 'rspSt', 1, 'rspSt');

			wspMetaUi.reg.addToList('srcFields:longDesc', 'rspUsrs', 1, 'rspUsrs');
		}

		if (!("hasTasks" in wspMetaUi)) wspMetaUi.hasTasks = hasTasks(wspMetaUi._dataItemTypes);
		if (wspMetaUi._itemLifeCycle || wspMetaUi.hasTasks) {
			//Ajout des champs shortDesc
			wspMetaUi.reg.addToList('srcFields:shortDesc', 'lcSt', 1, 'lcSt');
			//Ajout des champs longDesc
			wspMetaUi.reg.addToList('srcFields:longDesc', 'lcDt', 1, 'lcDt');
			wspMetaUi.reg.addToList('srcFields:longDesc', 'lcBy', 1, 'lcBy');
		}
		if (wspMetaUi.hasTasks) {
			//shortDesc
			wspMetaUi.reg.addToList('srcFields:shortDesc', 'actTi', 1, 'actTi'); //pour tasks
			if (wspMetaUi.flagItemsWithTasks) wspMetaUi.reg.addToList('srcFields:shortDesc', 'tkPending', 1, 'tkPending'); //flag sur items
			//longDesc
			wspMetaUi.reg.addToList('srcFields:longDesc', 'rspUsrs', 1, 'rspUsrs'); //pour tasks
			wspMetaUi.reg.addToList('srcFields:longDesc', 'actCts', 1, 'actCts'); //pour tasks
			wspMetaUi.reg.addToList('srcFields:longDesc', 'actStage', 1, 'actStage'); //pour tasks
			wspMetaUi.reg.addToList('srcFields:longDesc', 'tkDeadline', 1, 'tkDeadline'); //pour tasks
		}

		//TODO pour alléger les imports initiaux charge dynamiquement les libs complémentaires en fonction des
		// propriétés des itemsTypes effectivement déclarés. Exemples : remoteView, DynGen
		return wspMetaUi;
	}

	/** Construit le EndPoint de téléchargement d'un wspType.js, incluant ses préfix de résolution "wspRes" et "wspTypeRes" (pour les otions). */
	static buildWspTypeUiEndPoint(wspServer: WspSrv, wspType: JWspType, wspTypeRes?: IEndPoint): IEndPoint {
		//keyWspType + ".wui.wsp~"+lang+"#"+version+"/wspType.js"
		const a: string[] = [];
		a.push("show!", wspType.key, ".wui.wsp");
		if (wspType.lang) a.push("~", wspType.lang);
		a.push("%23", wspType.version, "%23", wspType.version, "/");
		const target = wspServer.config.adminWspUrl.resolve(a.join(""));
		const endPoint = wspServer.reg.env.resolver.clone().setBase(target).addEndPoint("wspRes", target);
		if (wspTypeRes) endPoint.addEndPoint("wspTypeRes", wspTypeRes).freeze();
		return endPoint;
	}

	/** Sous-registre du reg de l'univers chain propre à ce wspMetaUi. Sa config sera copié dans chaque wsp cible. */
	readonly reg: IReg<IChainEnv>;

	/** = reg.env.universe.wspServer.chain */
	readonly chain: ChainUniverse;

	/** URL du module js spécifiant le modèle documentaire. */
	readonly wspTypeUrl: IEndPoint;

	/** URLs des module js spécifiant les extensions du modèle documentaire. */
	readonly wspOptionUrls?: IEndPoint[];

	/** Types d'items connus indexés par leur itModel. */
	protected _dataItemTypes: Dict<IDatasItemType> = {};
	protected _itemTypesMap: Dict<ItemType> = {};
	protected _itemTypesSet: ItemType[];

	/** Map des définitions de fichiers Wed disponibles pour ce modèle documentaire. */
	protected _weds: Dict<IWedDef> = {};

	/** Cycle de vie pour les items (hors tasks). */
	protected _itemLifeCycle: {
		/** Intitulé de ce LC */
		lcName?: string;
		/** Liste des states possibles */
		lcStates: Map<string, IDatasLcState>;
		/** Liste des transitions possibles */
		lcTrans: Map<string, IDatasLcSimpleTransition>;
		/** Ordonnancement des groupes des transitions */
		lcTransGroupOrder?: string
	};

	/** EndPoint du wspType / wspOption en cours de configuration. */
	protected _currrentWspTypeEP: IEndPoint;

	/** ItemType pour une uri nulle. */
	protected _itemTypeNull: ItemType;
	get itemTypeNull(): ItemType {return this._itemTypeNull || (this._itemTypeNull = new ItemTypeNull(this))}

	/** ItemType pour un item non trouvé (uri spécifiée). */
	protected _itemTypeNotFound: ItemType;
	get itemTypeNotFound(): ItemType {return this._itemTypeNotFound || (this._itemTypeNotFound = new ItemTypeNotFound(this))}

	/**
	 * ItemType pour un item existant mais non reconnu dans le WspMeta.
	 * A ne pas confondre avec une itemType de type UndefinedPrim déclaré dans le modèle et présent dans la liste _itemTypes.
	 */
	protected _itemTypeFallback: ItemType;
	get itemTypeFallback(): ItemType {return this._itemTypeFallback || (this._itemTypeFallback = new ItemTypeFallback(this))}

	protected _icon: string = null;
	protected _defaultResIconFile: string;
	protected _defaultResIconFolder: string;
	protected _defaultResIconOpenFolder: string;
	protected _spaceIcon: string;
	protected _spaceOpenIcon: string;

	protected constructor(reg: IReg<IChainEnv>, wspTypeUrl: IEndPoint, wspOptionUrls: IEndPoint[]) {
		this.reg = REG.createSubReg(reg);
		this.chain = reg.env.universe.wspServer.chain;
		this.wspTypeUrl = wspTypeUrl;
		this.wspOptionUrls = wspOptionUrls;
	}

	/** Retourne un itemType par son itModel. */
	getItemType(itModel: string): ItemType {
		if (itModel == null) return this.itemTypeNotFound;
		let itemType = this._itemTypesMap[itModel];
		if (itemType === undefined) {
			const datas = this._dataItemTypes[itModel];
			if (datas) {
				switch (datas.family) {
				case EItemTypeFamily.xml:
					itemType = new ItemTypeXml(this, datas);
					break;
				case EItemTypeFamily.res:
					itemType = new ItemTypeRes(this, datas);
					break;
				case EItemTypeFamily.folder:
					itemType = new ItemTypeFolder(this, datas);
					break;
				case EItemTypeFamily.task:
					itemType = new ItemTypeTask(this, datas);
					break;
				case EItemTypeFamily.undef:
					itemType = new ItemTypeUndef(this, datas);
					break;
				default :
					itemType = new ItemTypeOther(this, datas);
				}
				if (datas.configItemType) itemType = datas.configItemType(itemType);
				this._itemTypesMap[itModel] = itemType;
			} else {
				this._itemTypesMap[itModel] = null;
			}
		}
		return itemType || this.itemTypeFallback;
	}

	/** Retourne tous les itemsTypes déclarés par le wspMeta (hors itemTypeFallback/Null/NotFound). */
	getItemTypes(): ItemType[] {
		if (this._itemTypesSet == null) {
			this._itemTypesSet = [];
			for (const md in this._dataItemTypes) {
				const data = this._dataItemTypes[md];
				if (data) this._itemTypesSet.push(this.getItemType(data.model));
			}
		}
		return this._itemTypesSet;
	}

	/** Le modèle doc contient-il des responsabilités ? */
	public hasResps: boolean;

	/** Le modèle doc contient-il des tasks ? */
	public hasTasks: boolean;

	/** Le modèle doc contient-il au moins une tâche planifiable. */
	hasForthcomingTasks(): boolean {
		for (const md in this._dataItemTypes) {
			const data = this._dataItemTypes[md] as IDatasTaskItemType;
			if (data?.family === EItemTypeFamily.task) {
				if (data.lcStates.find((st) => st.taskStage === ETaskStage.forthcoming)) return true;
			}
		}
		return false;
	}

	/**
	 * Activation de la mise 'en gras' es items avec des tâches en cours.
	 * Désactivé par défaut car cette information (field tkPending sur l'item) n'est pas correctement rafraichie
	 * via les remontées d'events.
	 */
	public flagItemsWithTasks: boolean;

	/** Retourne un set dédoublonné des resps déclarés dans les itemTypes
	 * 	FIXME : déclarer les resps comme des objets globaux dans le wspMetaUi, avec def de propriétés supp dans chaque itemDef (required)
	 */
	listResps(family: 'task' | 'item' = 'item'): IDatasResp[] {
		if (!this._listRespsTasks || !this._listRespsItems) {
			const mapTasks = new Map<String, IDatasResp>();
			const mapItems = new Map<String, IDatasResp>();
			for (const md in this._dataItemTypes) {
				const resps = this._dataItemTypes[md].resps;
				if (resps) {
					resps.forEach((resp) => {
						if (this._dataItemTypes[md].family === "task")
							mapTasks.set(resp.code, resp)
						else
							mapItems.set(resp.code, resp)
					})
				}
			}
			this._listRespsTasks = Array.from(mapTasks.values());
			this._listRespsItems = Array.from(mapItems.values());
		}
		return family === 'task' ? this._listRespsTasks : this._listRespsItems;
	}

	private _listRespsTasks: IDatasResp[];

	private _listRespsItems: IDatasResp[];

	getItemTypesTree(reducer?: (acc: ItemType[], cur: ItemType, idx: number, src: ItemType[]) => ItemType[]): (IDirItemType | ItemType)[] {
		function findOrCreateFolder(parent: (IDirItemType | ItemType)[], folder: IDatasDirItemType): IDirItemType {
			for (const ch of parent) {
				if (isDirItemType(ch) && ch.code === folder.code) {
					//todo merge IDatasDirItemType properties (surcharge des props du folder par les options), ajouter un level ?
					return ch;
				}
			}
			//Pas trouvé, on l'ajoute
			const dir = {
				code: folder.code,
				sortKey: folder.sortKey,
				label: folder.label,
				closed: folder.closed,
				children: []
			} as IDirItemType;
			parent.push(dir);
			return dir;
		}

		// 0- On filtre le tree
		let itemTypesFiltered: ItemType[];
		if (reducer) {
			itemTypesFiltered = [];
			this.getItemTypes().reduce(reducer, itemTypesFiltered);
		} else
			itemTypesFiltered = this.getItemTypes();

		// 1- on peuple le tree
		const tree = [] as (IDirItemType | ItemType)[];
		itemTypesFiltered.forEach((itemType) => {
			let parent = tree;
			const folders = itemType.datas.classification;
			if (folders != null) {
				for (const folder of folders) parent = findOrCreateFolder(parent, folder).children;
				parent.push(itemType);
			}
		})

		// 2- on tri
		function sort(parent: (IDirItemType | ItemType)[]) {
			parent.sort(function (e1: IDirItemType | ItemType, e2: IDirItemType | ItemType) {return e1.sortKey == e2.sortKey ? 0 : e1.sortKey > e2.sortKey ? 1 : -1});
			for (const ch of parent) if (isDirItemType(ch)) sort(ch.children);
		}

		sort(tree);
		return tree;
	}

	/** Fournit la liste des itModel vus comme des item-documents. */
	getDocModels(): string[] | undefined {
		let r: string[];
		for (const type of this.getItemTypes()) {
			if (type.datas.asDoc) {
				if (!r) r = [type.getModel()];
				else r.push(type.getModel());
			}
		}
		return r;
	}

	getDefaultResFileIcon(): string {
		if (!this._defaultResIconFile) this._defaultResIconFile = this.chain.config.skinUrl.url + "wsp/itemType/icons/resFile/icovalid.png";
		return this._defaultResIconFile;
	}

	getDefaultResFolderIcon(): string {
		if (!this._defaultResIconFolder) this._defaultResIconFolder = this.chain.config.skinUrl.url + "wsp/itemType/icons/resFolder/icovalid.png";
		return this._defaultResIconFolder;
	}

	getDefaultResFolderOpenIcon(): string {
		if (!this._defaultResIconOpenFolder) this._defaultResIconOpenFolder = this.chain.config.skinUrl.url + "wsp/itemType/icons/resFolderOpen/icovalid.png";
		return this._defaultResIconOpenFolder;
	}

	getSpaceIcon(src?: JSrcFields): string {
		if (!this._spaceIcon) this._spaceIcon = this.chain.config.skinUrl.url + "wsp/itemType/icons/space/icovalid.png";
		return this._spaceIcon;
	}

	getSpaceOpenIcon(src?: JSrcFields): string {
		if (!this._spaceOpenIcon) this._spaceOpenIcon = this.chain.config.skinUrl.url + "wsp/itemType/icons/spaceOpen/icovalid.png";
		return this._spaceOpenIcon;
	}

	/**
	 * Icône du wspDef / wspExt
	 */
	getIcon(): string | null {
		return this._icon;
	}

	getWedDef(key: string): IWedDef {
		const wed = this._weds[key];
		if (wed) return wed;
		return key === "rawXml" ? {path: ":back:modeling/rawXml.wed.xml"} : null;
	}

	getLcName(): string {
		return this._itemLifeCycle.lcName || LC_DEFAULT_NAME;
	}

	getLcStates(): Map<string, IDatasLcState> {
		return this._itemLifeCycle ? this._itemLifeCycle.lcStates : null;
	}

	getLcState(code: string): IDatasLcState {
		return this._itemLifeCycle ? this._itemLifeCycle.lcStates.get(code) : null;
	}

	getLcStateOrUnknown(code: string): IDatasLcState {
		return this._itemLifeCycle ? this._itemLifeCycle.lcStates.get(code) || this._itemLifeCycle.lcStates.get("?") : null;
	}

	getLcTransition(code: string): IDatasLcSimpleTransition {
		return this._itemLifeCycle ? this._itemLifeCycle.lcTrans.get(code) : null;
	}

	getLcTransitions(): Map<string, IDatasLcSimpleTransition> {
		return this._itemLifeCycle ? this._itemLifeCycle.lcTrans : null;
	}

	getLcTransitionsGroupOrder(): string {
		return this._itemLifeCycle ? (this._itemLifeCycle.lcTransGroupOrder || '*') : null;
	}

	/**
	 * Retourne une regExp sur les signatures des items pour sélectionner ceux qui possèdent au moins 1 générator ou un déploiement.
	 * ATTENTION : les perms sur les gen ne sont pas appliquées (dépend de chaque src, pas du niveau atelier).
	 */
	getGenOrCidSgnPattern(): RegExp {
		const hasGen = (gens: IDatasItemGenNode[]): boolean => {
			if (gens) for (let gen of gens) {
				if (gen.genNodeType === 'gen' || gen.genNodeType === 'cid') return true;
				if (gen.ch && hasGen(gen.ch)) return true;
			}
			return false;
		};
		let sgns = [] as string[];
		for (let itType of this.getItemTypes()) {
			let itData = itType.datas;
			if (hasGen(itData.generators)) sgns.push(itData.sgn);
		}
		return sgns.length > 0 ? new RegExp(sgns.join('|')) : null;
	}

	hasHelpDb(): boolean {return this._helpSrc != null || this._helpOptionsSrc != null}

	getHelpDbProv(): () => Promise<IHelpDb | null> {
		if (!this._helpDbProv) this._helpDbProv = async (): Promise<IHelpDb | null> => {
			const {getHelpLocalDb, HelpLocalDb} = await import("lib/help/helpLocalDb.js");
			let helpDb: HelpLocalDb = this._helpSrc ? getHelpLocalDb(this._helpSrc) : null;
			if (this._helpOptionsSrc) {
				const {HelpUnionDb} = await import("lib/help/helpUnionDb.js");
				const map = new Map<string, { db: IHelpDb, title: string }>();
				this._helpOptionsSrc.forEach((optHelp) => map.set(optHelp.key, {db: getHelpLocalDb(optHelp.endPoint, helpDb, optHelp.key), title: optHelp.title}));
				return new HelpUnionDb(this.reg, helpDb, map);
			}
			return helpDb;
		}
		return this._helpDbProv;
	}

	protected _helpDbProv: () => Promise<IHelpDb | null>;


	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	setItemType(dataItemType: IDatasItemType) {
		this._dataItemTypes[dataItemType.model] = dataItemType;
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	setWed(key: string, wedDef: IWedDef) {
		this._weds[key] = wedDef;
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	setSpaceIcon(close: string, open: string) {
		this._spaceIcon = close;
		this._spaceOpenIcon = open;
	}

	/** Icône associée au wspDef */
	setIcon(ico: string) {
		this._icon = ico;
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	setItemLcName(name: string) {
		if (!this._itemLifeCycle) this._itemLifeCycle = {lcStates: new Map(), lcTrans: new Map()};
		this._itemLifeCycle.lcName = name;
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	addItemLcState(...lcState: IDatasLcState[]) {
		if (!this._itemLifeCycle) this._itemLifeCycle = {lcStates: new Map(), lcTrans: new Map()};
		for (const st of lcState) {
			if (st.icon) st.iconUrl = this._currrentWspTypeEP.resolve(st.icon).url;
			this._itemLifeCycle.lcStates.set(st.code, st);
		}
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	addItemLcTrans(...lcTrans: IDatasLcSimpleTransition[]) {
		if (!this._itemLifeCycle) this._itemLifeCycle = {lcStates: new Map(), lcTrans: new Map()};
		for (const tr of lcTrans) {
			if (tr.icon) tr.iconUrl = this._currrentWspTypeEP.resolve(tr.icon).url;
			this._itemLifeCycle.lcTrans.set(tr.code, tr);
		}
	}

	/** Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()*/
	setItemLcTransGroupOrder(groupOrder: string) {
		if (!this._itemLifeCycle) this._itemLifeCycle = {lcStates: new Map(), lcTrans: new Map()};
		this._itemLifeCycle.lcTransGroupOrder = groupOrder;
	}

	/**
	 * Configure un système d'aide en ligne intégrée.
	 * Appelé par les wspType.js / wspOption.js, dans IWspTypeModule.configWspMeta()
	 * Les propriété key et title ne sont à utiliser que dans wspOption.js pour gérer l'agrégation dans
	 * une Db unifiée.
	 */
	addLocalHelp(source: string, key?: string, title?: string) {
		if (!key) {
			this._helpSrc = this._currrentWspTypeEP.resolve(source);
		} else {
			if (!this._helpOptionsSrc) this._helpOptionsSrc = [];
			this._helpOptionsSrc.push({endPoint: this._currrentWspTypeEP.resolve(source), key, title});
		}
	}

	/** URLs des sources d'aides */
	protected _helpSrc: IEndPoint;
	protected _helpOptionsSrc: { endPoint: IEndPoint, key: string, title?: string }[];

	toString() {
		return JSON.stringify({wspType: this.wspTypeUrl.url, wspOptions: this.wspOptionUrls?.map((v) => v.url)})
	}

}

/** Familles de type d'items. */
export const enum EItemTypeFamily {
	'xml' = 'xml',
	'res' = 'res',
	'folder' = 'folder',
	'task' = 'task',
	'undef' = 'undef',
	'other' = 'other'
}

/** Défini un type d'item. */
export abstract class ItemType {

	/** reg dédié à cet itemType. */
	reg: IReg<IItemTypeEnv>;

	protected _schema: Promise<Schema>;

	protected _iconPngValid: string;

	protected _iconPngOpenValid: string;

	constructor(public readonly wspMeta: WspMetaUi, public readonly datas: IDatasItemType) {
		this.reg = REG.createSubReg<IItemTypeEnv>(wspMeta.reg);
		this.reg.env.itemType = this;
	}

	getFamily(): EItemTypeFamily | null {return this.datas.family}

	isFamily(...family: EItemTypeFamily[]): boolean {
		return family.indexOf(this.datas.family) >= 0;
	}

	getModel(): string {return this.datas.model}

	getSgn(): string {return this.datas.sgn}

	getTitle(shortDesc?: JSrcFields): string {return this.datas.title}

	/** Pour les items xml, retourne le nodeName racine de cet item. */
	getTagRoot(): string {return null}

	getIcon(shortDesc?: JSrcFields): string {
		// Controle erreur "itSt"
		if (shortDesc) switch (shortDesc.itSt) {
		case EItStatus.warnings:
			return this.datas.wspResUrl.resolve(this.datas.iconsPng + "icowarning.png").url;
		case EItStatus.errors:
		case EItStatus.conflict:
			return this.datas.wspResUrl.resolve(this.datas.iconsPng + "icoerror.png").url;
		}
		// Controle erreur "rspSt"
		if (shortDesc) switch (shortDesc.rspSt) {
		case EItResp.errors:
			return this.datas.wspResUrl.resolve(this.datas.iconsPng + "icoerror.png").url;
		}
		if (!this._iconPngValid) this._iconPngValid = this.datas.wspResUrl.resolve(this.datas.iconsPng + "icovalid.png").url;
		return this._iconPngValid;
	}

	getIconOpen(shortDesc?: JSrcFields): string {
		// Controle erreur "itSt"
		if (shortDesc) switch (shortDesc.itSt) {
		case EItStatus.warnings:
			return this.datas.wspResUrl.resolve((this.datas.iconsPngOpen || this.datas.iconsPng) + "icowarning.png").url;
		case EItStatus.errors:
		case EItStatus.conflict:
			return this.datas.wspResUrl.resolve((this.datas.iconsPngOpen || this.datas.iconsPng) + "icoerror.png").url;
		}
		// Controle erreur "rspSt"
		if (shortDesc) switch (shortDesc.rspSt) {
		case EItResp.errors:
			return this.datas.wspResUrl.resolve((this.datas.iconsPngOpen || this.datas.iconsPng) + "icoerror.png").url;
		}
		if (!this._iconPngOpenValid) this._iconPngOpenValid = this.datas.wspResUrl.resolve((this.datas.iconsPngOpen || this.datas.iconsPng) + "icovalid.png").url;
		return this._iconPngOpenValid;
	}

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {throw Error(/*to implement*/)}

	configRegForMainView(ctx: ISrcMainCtx, reg: IReg<IItemUiEnv>) {
		const configReg = this.getDataSrcMainView(ctx, true)?.configReg;
		if (configReg) configReg(reg);
		else if (ctx.mainViewCode) {
			if (ctx.mainViewCode.indexOf(":hideWspStruct:") >= 0) {
				reg.setPref("wsp.hideWspStruct", REG.LEVELAUTH_MODEL, true);
				reg.addToList("ribbon:item", "refs", REG.LEVELAUTH_MODEL, null);
				// reg.addToList("actions:wsp:shortDesc", "renameSrc", REG.LEVELAUTH_MODEL, null);
				// reg.addToList("accelkeys:wsp:shortDesc", "F2", 1, null);
			}
		}
	}

	getConvertersForMainView(ctx: ISrcMainCtx): IAction<IRegPointer<IItemUiEnv>>[] | null {
		const cnvs = this.getDataSrcMainView(ctx, true)?.convertors;
		if (!cnvs) return null;
		for (let i = 0; i < cnvs.length; i++) {
			const cnv = cnvs[i];
			if ('executor' in cnv) cnvs[i] = new ItemMainConvert(cnv);
		}
		// EVOL : prévoir une clef de trie explicite (besoin : liste construite par le wspDef mais aussi potentiellement par l'extDef)
		cnvs.sort((c1: IAction<IRegPointer<IItemUiEnv>>, c2: IAction<IRegPointer<IItemUiEnv>>) => c1.getLabel(ctx).localeCompare(c2.getLabel(ctx)))
		return cnvs as IAction<IRegPointer<IItemUiEnv>>[];
	}

	getDataSrcMainView(ctx: ISrcMainCtx, strict?: boolean): IDataSrcMainView {
		if (!ctx.mainViewCode) return this.datas.mainView;
		return this.datas.otherMainViews ? this.datas.otherMainViews[ctx.mainViewCode] || (strict ? null : this.datas.mainView) : strict ? null : this.datas.mainView;
	}

	getIsolatedView(shortDesc: JSrcFields): IDatasSrcView | null {
		return this.datas.isolatedView;
	}

	getMainStreamSrcUri(shortDesc: JSrcFields): srcUri | null {
		return shortDesc ? shortDesc.srcUri : null;
	}

	getMainXmlSrcUri(shortDesc: JSrcFields): string | null {
		return shortDesc ? shortDesc.srcUri : null;
	}

	getSchema(): Promise<Schema> {
		if (!this.datas.schema) return getGenericSchema();
		if (!this._schema) this._schema = getSchema(this.datas.wspResUrl.resolve(this.datas.schema));
		return this._schema;
	}

	/**
	 * Si code null ou "", retourne l'éditeur principal de la mainView.
	 * (à voir si on rend explicite un éditeur par défaut utilisé pour afficher l'éditeur en isolé).
	 */
	getEditor(code?: string | null): IDatasEditor | null {
		if (code) return this.finalizeDatasEditor(this.datas.editors[code]);
		if (this.datas.mainView) return this.finalizeDatasEditor(this.datas.editors[this.datas.mainView.xmlEditor]);
		return null;
	}

	protected finalizeDatasEditor(d: IDatasEditor | null): IDatasEditor | null {
		if (d?.help && !d.help.helpDbProv) {
			d.help.helpDbProv = this.wspMeta.getHelpDbProv();
			if (d.help.outlineView === undefined) d.help.outlineView = {indexKey: "home"};
		}
		return d;
	}

	getWed(key: string): IEndPoint & IPathResolver {
		const wedDef = this.wspMeta.getWedDef(key);
		if (!wedDef) throw Error(`Wed editor key '${key}' do not exist in this wspMetaUi ${this.wspMeta.toString()}`);
		let endP = this.datas.wspResUrl.resolve(wedDef.path);
		if (!(endP instanceof EndPointResolver)) endP = this.reg.env.resolver.clone().setBase(endP).addEndPoint("wspRes", this.datas.wspResUrl);
		return endP as EndPointResolver;
	}

	getPreview(wsp: Wsp, fields: JSrcFields, reg: IReg<IWspUiEnv> | null, utils: typeof UTILS): TemplateResult | undefined {
		const preview = this.datas.previewMini;
		if (!preview) return undefined;
		if (preview.tag === "wsp-dyngen-preview") {
			// /wspCode/cdGen/refUri'xxx'skin'yy'/co
			if (fields.srcSt < 0 || ITEM.isHistoryOrTrashUri(fields.srcUri)) return undefined;
			//const url = wsp.wspServer.config.itemDynGenUrl.url + `${encodeURIComponent(wsp.code)}/${preview.codeGen}/${encodeURIComponent(CDM.stringify({refUri: DYNGEN.encodeUrl2(SRC.srcRef(fields)), ...preview.params}))}/co/`;
			const dynGen = new DynGen().initialize({
				reg: reg || this.reg,
				w: wsp.code,
				c: preview.codeGen,
				r: ITEM.srcRefSub(fields),
				gp: preview.params,
			});
			dynGen.onViewShown();
			DOM.setAttr(dynGen, "style", (preview.w ? `width:${preview.w};` : '') + (preview.h ? `height:${preview.h};` : ''));
			DOM.setAttr(dynGen, "class", "itemPreview dynGenView");
			return xhtml`${dynGen}`;
		} else if (preview.tag === "wsp-remote-view") {
			return xhtml`<wsp-remote-view class="itemPreview" style="${(preview.w ? `width:${preview.w};` : '') + (preview.h ? `height:${preview.h};` : '')}" p.wsp="${wsp}" p.fields="${fields}" i.="${preview.init}"/>`;
		} else if (preview.tag === "wsp-code-view") {
			return xhtml`<wsp-code-view class="itemPreview" p.wsp="${wsp}" p.fields="${fields}" p.transform="${preview.transform}" i.="${preview.init}"/>`;
		}

		const url = preview.transform ? WSP.getStreamStampedUrl(wsp, SRC.srcRef(fields), fields.srcDt, preview.transform) : WSP.getStreamStampedUrl(wsp, this.getMainStreamSrcUri(fields), fields.srcDt);
		switch (preview.tag) {
		case 'img':
			return xhtml`<img class="itemPreview imgView" src="${url}"/>`;
		case 'video':
			if (preview.poster) {
				const poster = wsp.wspServer.config.wspSrcUrl + IO.qs("cdaction", "GetSrc", "param", wsp.code, "format", 'stream', "refUri", SRC.srcRef(fields), "transform", preview.poster, "t", fields.srcDt);
				return xhtml`<video class="itemPreview videoView" src="${url}" poster="${poster}" controls="controls" style="${(preview.w ? `width:${preview.w};` : '') + (preview.h ? `height:${preview.h};` : '')}" />`;
			} else {
				return xhtml`<video class="itemPreview videoView" src="${url}" controls="controls" style="${(preview.w ? `width:${preview.w};` : '') + (preview.h ? `height:${preview.h};` : '')}"/>`;
			}
		case 'audio':
			return xhtml`<audio class="itemPreview audioView" src="${url}" controls="controls"/>`;
		case 'iframe':
			return xhtml`<iframe class="itemPreview iframeView" style="${(preview.w ? `width:${preview.w};` : '') + (preview.h ? `height:${preview.h};` : '')}" src="${url}"/>`;
		}
		return undefined;
	}

	/** Retourne la ou les extensions autorisées. Ex: ".png", ".xml". */
	getExtensions(contentType?: string): string | string[] {
		const ext = this.datas.itemExt;
		if (Array.isArray(ext)) return ext.length > 1 ? ext : ext[0];
		return ext || ".xml"
	}

	/** Résoud un path dans le contexte du pack de déclaration de cet itemType. */
	resolvePath(path: string): IEndPoint {return this.datas.wspResUrl.resolve(path)}

	/**
	 * Evalue la concordance avec un fichier externe en cours d'import (drad session, presse-papier)
	 * <br/>Retourne une certitude :
	 * <ul>
	 * <li>-1 = ne matche pas</li>
	 * <li>0 = n'a pas pu être déterminé</li>
	 * <li>1 = undefPrim</li>
	 * <li>2 = match de type "toutes les extensions", ou "tous les types" (sauf éventuellement une liste noire)</li>
	 * <li>100 = match avec certitude maximale</li>
	 * </ul>
	 */
	matchType(contentType?: string, fileName?: string): number {return -1}

	/**
	 * Evalue si l'extension de fileName appartient à la liste des extensions de cet itemType
	 * Retourne une certitude :
	 * <ul>
	 * <li>-1 = ne matche pas</li>
	 * <li>0 = n'a pas pu être déterminé</li>
	 * <li>1 = undefPrim</li>
	 * <li>2 = match de type "toutes les extensions", ou "tous les types"</li>
	 * <li>100 = match avec certitude maximale</li>
	 * </ul>
	 */
	matchExt(fileName?: string): number {
		if (!fileName) return 0;
		const exts = this.getExtensions();
		if (exts == null) return 2;
		if (Array.isArray(exts)) return exts.find((entry) => SRC.matchExt(fileName, entry)) ? 100 : -1;
		return SRC.matchExt(fileName, exts) ? 100 : -1
	}

	/**
	 * Pour les items de type "Document", fournit la liste des itModel vus comme des supports
	 * de générateurs de cet item-document.
	 * Retourne null si cet itemType n'a pas d'itemTypes de support associés mais directement des générateurs.
	 */
	getSupportModels(): string[] | null {
		if (!this.datas.asDoc) return null;
		const supports = this.datas.asDoc.supports;
		if (!supports) return null;
		return supports.map((s) => s.itModel);
	}

	/** Pour les items de type "Document", fournit son éditeur masquant son lien vers son document. */
	getSupportEditor(support: ItemType): IDatasEditor {
		const sup = this.datas.asDoc.supports.find((s) => s.itModel === support.getModel());
		return sup ? support.datas.editors[sup.keyEditor] : null;
	}

	/**
	 * Retourne un name par défaut pour ce type d'item et cette extension (itemCode = itemName + itemExtension).
	 *
	 * @param currentSpace Si null, création d'un code pour airItem, donc pas de check d'existance sur le serveur.
	 * @param currentExt Extension sélectionnée pour cet ItemType (Ex:"png").
	 */
	async buildItemName(wsp: Wsp, currentSpace: null | string, currentExt: string, isFree?: (itemCode: string) => Promise<boolean>): Promise<string> {
		let tpl = this.datas.itemNameTpl || "{folder}_{counter}";
		tpl = tpl.replace(/(\{folder\})/g, currentSpace ? SRC.extractLeafFromUri(currentSpace) : "");
		if (tpl.indexOf("{dateStamp") > -1) {
			function str(num: number): string {return num > 9 ? num.toString() : '0' + num}

			const format = tpl.match(/\{dateStamp ([^\}]*)\}/)[1] || "%Y-%m-%d";
			const d = new Date();
			const date = format.replace(/(%.)/g, (k: string): string => {
				switch (k) {
				case "%Y":
					return d.getFullYear().toString();
				case "%m":
					return str(d.getMonth() + 1);
				case "%d":
					return str(d.getDate());
				case "%H":
					return str(d.getHours());
				case "%M":
					return str(d.getMinutes());
				case "%S":
					return str(d.getSeconds());
				}
				return k
			});
			tpl = tpl.replace(/(\{dateStamp [^\}]*\})/g, date);
		}
		let idx = 1;
		let name = tpl;
		while (true) {
			name = tpl.replace(/(\{optionalCounter\})/g, (idx > 1 ? "_" + (idx < 10 ? "0" + idx : idx) : ""));
			name = name.replace(/(\{counter\})/g, idx < 10 ? "0" + idx : idx.toString());
			if (name === tpl) break; //pas de compteur dans le template, on accepte ce name sans recherche d'un code unique.
			if (!isFree) break; //pas de controle si l'uri est dispo.
			const itemCode = name + currentExt;
			if (ITEM.isValidCodeItem(itemCode)) {
				//le template a produit un nom valide
				if (isFree && await isFree(itemCode)) break;
				idx++;
			} else {
				//le template a produit un nom invalide
				break;
			}
		}
		return name;
	}

	/**
	 * Retourne le body à utiliser pour créer un nouvel item mono-flux ou une structure de IFolder.
	 * Impl par défaut : fichier vide.
	 * @return
	 * 	- null: action abandonnée
	 * 	- "": fichier vide, 0 octets
	 * 	- {name:codeItem, entries:[]}: dossier vide
	 */
	async getNewContent(reg: IReg<IWspUiEnv>, space: null | string, codeItem: string, options?: ONewContent): Promise<IBody | IFolder | null> {
		return "";
	}


	hasGenerators(secCtx: ISecurityCtx) {
		if (this.datas.generators) for (const gen of this.datas.generators) {
			if (!gen.perms || this.reg.hasPerm(gen.perms, secCtx)) return true;
		}
		return false;
	}

	/** Construit un arbre de IItemPubNode en fonction des permissions courantes.*/
	buildPubTree(secCtx: ISecurityCtx, filter?: (it: IDatasItemGenNode | IDatasItemCidNode) => boolean): IItemPubNode[] {
		const cloneList = (from: (IDatasItemGenNode & IDatasItemCidNode)[], to: IItemPubNode[]): IItemPubNode[] => {
			if (from) for (const datas of from) {
				if (datas.perms && !this.reg.hasPerm(datas.perms, secCtx)) continue;
				if (filter && !filter(datas)) continue;
				if (datas.genNodeType !== 'dir') {
					if (!datas.iconUrl) {
						if (datas.icon) datas.iconUrl = this.datas.wspResUrl.resolve(datas.icon).url;
						else datas.iconUrl = `/@skin@/wsp/views/generators/gen_${datas.genNature || 'cid'}.svg`;
					}
					if (!datas.illusUrl) {
						if (datas.illus) datas.illusUrl = this.datas.wspResUrl.resolve(datas.illus).url;
						else datas.illusUrl = `/@skin@/wsp/views/generators/illus_${datas.genNature}.png`;
					}
				}
				const genNode = Object.create(datas) as IItemPubNode;
				if (datas.ch) {
					const children = cloneList(datas.ch, []);
					if (datas.genNodeType === 'dir' && children.length === 0) continue; //un dir sans fils, on saute
					genNode.ch = children.length > 0 ? children : null;
				}
				to.push(genNode);
			}
			return to;
		};

		return this.datas.generators ? cloneList(this.datas.generators, []) : [];
	}

	/** Recherche un générator déclaré par son code dans une liste donnée. */
	getPub(codeGenStack: string, list: IDatasItemPubNode[]): IDatasItemPubNode {
		if (!list) return null;
		let found = list.find((g) => g.codeGenStack === codeGenStack);
		if (found) return found;
		for (const child of list) {
			if (child.ch) {
				found = this.getPub(codeGenStack, child.ch);
				if (found) return found;
			}
		}
		return null;
	}

	/** Responsabilities : retourne true si cet itemType gère les responsabilities. */
	hasResps(): boolean {
		return this.datas.resps && this.datas.resps.length > 0;
	}

	/** Responsabilities : retourne la configuration d'une responsabilité sur cet item ou task. */
	getResp(code: string): IDatasResp {
		const resps = this.datas.resps;
		if (resps) for (const r of resps) if (r.code === code) return r;
		return null;
	}

	/** Utilisé par les extensions pour ajouter ou remplacer un éditor. */
	setEditor(key: string, editor: IDatasEditor) {
		(this.datas.editors || (this.datas.editors = {}))[key] = editor;
	}

	/** Utilisé par les extensions pour remplacer ou insérer un générateur dans l'arbre. */
	addGenerator(genNode: IDatasItemGenNode | IDatasItemCidNode, parentCodeGenStack?: string, insertBefore?: RegExp | string) {
		this.removeGenerator(genNode.codeGenStack);
		const parent = parentCodeGenStack ? this.getPub(parentCodeGenStack, this.datas.generators) : null;
		const list = parent ? parent.ch || (parent.ch = []) : this.datas.generators || (this.datas.generators = []);
		const next = insertBefore ? list.findIndex((g) => g.codeGenStack.match(insertBefore)) : -1;
		if (next < 0) list.push(genNode);
		else list.splice(next, 0, genNode);
	}

	/** Utilisé par les extensions pour ajouter un convertor */
	addConvertor(convertor: IDatasItemConvertor | IAction<IRegPointer<IItemUiEnv>>, ctx: ISrcMainCtx = {} as ISrcMainCtx) {
		const mainview = this.getDataSrcMainView(ctx, true);
		if (mainview) {
			let cnvs = mainview.convertors;
			if (!cnvs) cnvs = mainview.convertors = []
			cnvs.push(convertor);
		}
	}

	/** Utilisé par les extensions pour supprimer un générateur (voir un dir complet) dans l'arbre. */
	removeGenerator(codeGenStack: string, list: (IDatasItemGenNode | IDatasItemCidNode)[] = this.datas.generators || []): boolean {
		const idx = list.findIndex((g) => g.codeGenStack === codeGenStack);
		if (idx >= 0) {
			list.splice(idx, 1);
			return true;
		}
		for (const child of list) {
			if (child.ch) if (this.removeGenerator(codeGenStack, child.ch)) return true
		}
		return false;
	}

	/** Utilisé par les extensions pour remplacer ou insérer un subTab dans la mainView. */
	addMainViewSubTab(itemTab: IDatasItemTab, insertBefore?: string) {
		this.removeMainViewSubTab(itemTab.code);
		let mv = this.datas.mainView;
		if (!mv) mv = this.datas.mainView = {};
		let sts = mv.subTabs;
		if (!sts) sts = mv.subTabs = [];
		const next = insertBefore ? sts.findIndex((st) => st.code === insertBefore) : -1;
		if (next < 0) sts.push(itemTab);
		else sts.splice(next, 0, itemTab);
	}

	/** Utilisé par les extensions pour supprimer un subTab dans la mainView. */
	removeMainViewSubTab(code: string): boolean {
		const mv = this.datas.mainView;
		if (mv && mv.subTabs) {
			const idx = mv.subTabs.findIndex((st) => st.code === code);
			if (idx >= 0) {
				mv.subTabs.splice(idx, 1);
				return true;
			}
		}
		return false;
	}

	/** Utilisé par les extensions pour forcer un itemType existant en tant que "document". */
	setAsDoc() {
		if (!this.datas.asDoc) this.datas.asDoc = {};
	}

	/** Utilisé par les extensions pour que cet item-type soit vu comme un "support" du document docModel.
	 *    Contexte : itemType *support*
	 */

	/* non utilisé
	addDocument(docModel: ItemType, keyEditor: string, newContentBuilder: (wsp: Wsp, space: null | string, codeItem: string, options?: ONewContent) => Promise<IBody | null>) {
		docModel.setAsDoc();
		(docModel.datas.asDoc.supports || (docModel.datas.asDoc.supports = [])).push({
			itModel: this.getModel(),
			keyEditor,
			newContentBuilder
		});
	}
	*/

	/** Utilisé par les extensions pour ajouter un support au "document" courant
	 *    Contexte : itemType *document*
	 */
	addDocumentSupport(supportModel: ItemType, keyEditor: string, newContentBuilder: (wsp: Wsp, space: null | string, codeItem: string, options?: ONewContent) => Promise<IBody | null>) {
		this.setAsDoc();
		(this.datas.asDoc.supports || (this.datas.asDoc.supports = [])).push({
			itModel: supportModel.getModel(),
			keyEditor,
			newContentBuilder
		});
	}


	/** interne pour, algo de tri de l'arbre. */
	get sortKey() {return this.datas.sortKey}
}


export class ItemTypeXml extends ItemType {

	protected _tagRoot: string;

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {return this.datas.mainView.resView ? defaultItemMainAreaRes : defaultItemMainAreaXml}

	getTagRoot(): string {
		if (this._tagRoot === undefined) this._tagRoot = this.getSgn().replace(/@([^_]*)_([^#]*).*/, "$1:$2");
		return this._tagRoot;
	}

	matchType(type: string, fileName?: string): number {
		if (!type && !fileName) return 0;
		// Ext
		let matchExt = this.matchExt(fileName);
		if (matchExt != 0)
			return matchExt;
		// Mime
		if (["application/xml", "text/xml"].indexOf(type) > -1)
			return 100;
		return -1
	}

	async getNewContent(reg: IReg<IWspUiEnv>, space: null | string, codeItem: string, options?: ONewContent): Promise<IBody | null> {
		if (options?.docShortDesc) {
			//Création d'un support associé au document.
			const docType = this.wspMeta.getItemType(options.docShortDesc.itModel);
			const supDef = docType.datas.asDoc.supports.find((supDef) => supDef.itModel === this.getModel());
			return supDef.newContentBuilder(reg.env.wsp, space, codeItem, options);
		}
		const schema = await this.getSchema();
		if (!schema) return "<x/>";
		const doc = JML.jmlToDom(schema.createNewDoc());
		if (this.datas.createWithCtIdInAtt) {
			//Interrogation du svc ctId du wsp
			const wsp = reg.env.wsp;
			const id = await wsp.wspServer.config.wspSvcUrl.resolve(wsp.code + "/ctId").fetchText(IO.qs("param", codeItem), {method: "POST"});
			doc.documentElement.setAttribute(this.datas.createWithCtIdInAtt, id);
		}
		if (options?.remoteUrl) {
			//RemotePrim
			const scLocation = DOM.findNext(doc.documentElement, doc.documentElement, (n: Node): n is Element => n.nodeName === "sc:location");
			if (scLocation) scLocation.textContent = options.remoteUrl;
		}
		return DOM.ser(doc);
	}

}

/** Spécialisation d'un ItemType de famille 'res'. */
export interface IDatasItemTypeRes extends IDatasItemType {
	/** ressource qui n'est pas embedded dans un folder. Usage : modeling */
	withoutFolderMorphology?: boolean
}
class ItemTypeRes extends ItemType {
	static PATH_META = "meta.xml";

	constructor(wspMeta: WspMetaUi, public readonly datas: IDatasItemTypeRes) {
		super(wspMeta, datas);
		this.reg.addToList("actions:item:burger", "import", 1, ImportRes.SINGLETON(), 100);
		this.reg.addToList("actions:item:burger", "download", 1, ExportRes.SINGLETON(), 101);
	}

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {return defaultItemMainAreaRes}

	getExtensions(contentType?: string): string | string[] {
		const resTypes = this.datas.resTypes;
		if (!resTypes) return null;
		const exts = resTypes.reduce((acc, prev) => {
			if (!contentType || prev.ct.indexOf(contentType) >= 0) acc.push(...prev.ext);
			return acc;
		}, []);
		return exts.length < 2 ? exts[0] : exts;
	}

	async getNewContent(reg: IReg<IWspUiEnv>, space: null | string, codeItem: string): Promise<IBody | null> {
		const emptyUrls = this.datas.resEmptyUrl;
		if (emptyUrls) {
			for (const k of Object.keys(emptyUrls)) {
				if (SRC.matchExt(codeItem, k)) return this.datas.wspResUrl.resolve(emptyUrls[k]).fetchBlob();
			}
			if (emptyUrls['*']) return this.datas.wspResUrl.resolve(emptyUrls['*']).fetchBlob();
		}
		return "";
	}

	getMainStreamSrcUri(shortDesc: JSrcFields) {
		if (!shortDesc) return null;
		if (this.datas.withoutFolderMorphology)
			return shortDesc.srcUri
		switch (ITEM.getUriResType(shortDesc)) {
		case ITEM.EUriResType.root :
			return SRC.addLeafToUri(shortDesc.srcUri, SRC.extractLeafFromUri(shortDesc.srcUri));
		case ITEM.EUriResType.folder :
			return null;
		}
		return shortDesc.srcUri;
	}

	getIsolatedView(shortDesc: JSrcFields): IDatasSrcView | null {
		if (ITEM.extractItemResPath(shortDesc.srcUri) == ItemTypeRes.PATH_META) {
			//Vue isolée des metas.
			return {tag: "wsp-item-xmled"}
		}
		return this.datas.isolatedView;
	}

	getMainXmlSrcUri(shortDesc: JSrcFields): string | null {
		if (!shortDesc) return null;
		if (this.datas.withoutFolderMorphology)
			return shortDesc.srcUri;
		return ITEM.getUriResType(shortDesc) === ITEM.EUriResType.root ? SRC.addLeafToUri(shortDesc.srcUri, ItemTypeRes.PATH_META) : shortDesc.srcUri;
	}

	matchType(type?: string, fileName?: string): number {
		if (!this.datas.resTypes && !this.datas.resExtsMatcher) {
			//Toutes les extensions
			if (fileName) return fileName.indexOf(".") > 0 ? 2 : -1; //le name d'un item doit toujours contenir un "."
			return 2; //pas d'info on refusera après.
		}
		if (!type && !fileName) return 0;
		// resExtsMatcher
		if (!this.datas.resTypes && this.datas.resExtsMatcher) {
			if (!fileName) return 0;
			return this.datas.resExtsMatcher.test(fileName) ? 2 : -1;
		}
		// Ext
		let matchExt = this.matchExt(fileName);
		if (matchExt != 0)
			return matchExt;
		// Type
		for (const resType of this.datas.resTypes) {
			if (resType.ct) for (const t of resType.ct) if (t === type) return 100;
		}
		return -1;
	}
}

/** Spécialisation d'un ItemType de famille 'folder'. */
class ItemTypeFolder extends ItemType {

	constructor(wspMeta: WspMetaUi, datas: IDatasItemType) {
		super(wspMeta, datas);
		this.reg.addToList("actions:item:burger", "import", 1, ImportFolder.SINGLETON(), 100);
	}

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {return defaultItemMainAreaFolder}

	getMainStreamSrcUri(shortDesc: JSrcFields) {
		if (shortDesc && ITEM.getUriResType(shortDesc) === ITEM.EUriResType.file) return shortDesc.srcUri;
		return null;
	}

	getMainXmlSrcUri(shortDesc: JSrcFields): string | null {
		return null;
	}

	async getNewContent(reg: IReg<IWspUiEnv>, space: string | null, codeItem: string, options?: ONewContent): Promise<IBody | IFolder | null> {
		return {name: codeItem, entries: []};
	}

	getIcon(shortDesc?: JSrcFields): string {
		if (shortDesc) switch (ITEM.getUriResType(shortDesc)) {
		case ITEM.EUriResType.folder :
			return this.datas.iconResFolder || this.wspMeta.getDefaultResFolderIcon();
		case ITEM.EUriResType.file :
			return this.datas.iconResFile || this.wspMeta.getDefaultResFileIcon();
		}
		return super.getIcon(shortDesc);
	}

	getIconOpen(shortDesc?: JSrcFields): string {
		if (shortDesc) switch (ITEM.getUriResType(shortDesc)) {
		case ITEM.EUriResType.folder  :
			return this.datas.iconResFolderOpen || this.wspMeta.getDefaultResFolderOpenIcon();
		case ITEM.EUriResType.file:
			return this.datas.iconResFile || this.wspMeta.getDefaultResFileIcon();
		}
		return super.getIconOpen(shortDesc);
	}

	matchType(type?: string, fileName?: string): number {
		if (type) return -1; //Si mimeType recherché, on élimine les items folders.
		if (!fileName) return 0;
		if (!this.datas.itemExt) return 2; //Toutes les extensions
		if (typeof this.datas.itemExt === "string") return fileName.endsWith(this.datas.itemExt) ? 100 : -1;
		return this.datas.itemExt.find((ext) => fileName.endsWith(ext)) ? 100 : -1;
	}
}

/**
 * ItemType pour les tasks.
 */
export class ItemTypeTask extends ItemType implements ILcProvider {

	datas: IDatasTaskItemType;

	protected _descriptionSchema: Promise<Schema>;

	protected _commentSchema: Promise<Schema>;

	protected _lcStates: Map<string, IDatasLcState>;
	protected _lcTrans: Map<string, IDatasLcSimpleTransition>;
	protected _createTrans: IDatasLcSimpleTransition[];

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {return defaultTaskMainArea}

	getCommentSchema(): Promise<Schema> {
		if (!this.datas.commentSchema) return getGenericSchema();
		if (!this._commentSchema) this._commentSchema = getSchema(this.datas.wspResUrl.resolve(this.datas.commentSchema));
		return this._commentSchema;
	}

	getDesriptionSchema(): Promise<Schema> {
		if (!this.datas.descriptionSchema) return getGenericSchema();
		if (!this._descriptionSchema) this._descriptionSchema = getSchema(this.datas.wspResUrl.resolve(this.datas.descriptionSchema));
		return this._descriptionSchema;
	}

	getLcName(): string {
		return this.datas.lcName || LC_DEFAULT_NAME;
	}

	getLcStates(): Map<string, IDatasLcState> {
		this.buildLcStates();
		return this._lcStates;
	}

	getLcState(code: string): IDatasLcState {
		this.buildLcStates();
		return this._lcStates.get(code);
	}

	getLcStateOrUnknown(code: string): IDatasLcState {
		this.buildLcStates();
		return this._lcStates.get(code) || this._lcStates.get("?");
	}

	getLcTransitions(): Map<string, IDatasLcSimpleTransition> {
		this.buildLcTrans();
		return this._lcTrans;
	}

	getLcTransition(code: string): IDatasLcSimpleTransition {
		this.buildLcTrans();
		return this._lcTrans.get(code);
	}

	getLcTransitionsGroupOrder(): string {
		this.buildLcStates();
		return this._lcTrans ? (this.datas.lcTransGroupOrder || '*') : null;
	}

	/** Transitions pour la création d'une task. */
	getCreateTransitions(): IDatasLcSimpleTransition[] {
		this.buildCreateTrans();
		return this._createTrans;
	}

	/** Etat initial d'une task à la création. */
	getTaskInitStates(ctx: ITaskCreationCtx): JTaskStates {
		const r: JTaskStates = {};
		const acts = this.datas.onCreate;
		if (acts) for (const act of acts) {
			if ('addUserToResp' in act) {
				if (ctx.reg.env.universe.auth.currentAuthenticatedUser) {
					//user bien authentifé.
					if (!r.resps) r.resps = {};
					r.resps[act.addUserToResp] = [ctx.reg.env.universe.auth.currentAccount];
				}
			} else if ('detailsTpl' in act) {
				if (ctx.shortDescs.length > 0) {
					r.desc = act.detailsTpl.replace("${currentItemUri}", SRC.srcRef(ctx.shortDescs[0]));
				}
			} else if (typeof act === 'function') {
				act(ctx, this, r);
			}
		}
		return r;
	}

	protected buildLcStates() {
		if (!this._lcStates) {
			this._lcStates = new Map();
			for (const st of this.datas.lcStates) {
				if (st.icon) st.iconUrl = this.datas.wspResUrl.resolve(st.icon).url;
				this._lcStates.set(st.code, st);
			}
		}
	}

	protected buildLcTrans() {
		if (!this._lcTrans) {
			this._lcTrans = new Map();
			for (const trans of this.datas.lcTrans) {
				if (trans.icon) trans.iconUrl = this.datas.wspResUrl.resolve(trans.icon).url;
				this._lcTrans.set(trans.code, trans);
			}
		}
	}

	protected buildCreateTrans() {
		if (!this._createTrans) {
			this._createTrans = [];
			for (const trans of this.datas.createTrans) {
				if (trans.icon) trans.iconUrl = this.datas.wspResUrl.resolve(trans.icon).url;
				this._createTrans.push(trans);
			}
		}
	}

	/** Retourne true si cet it gère la feature. */
	hasFeature(feature: ETaskFeatures): boolean {
		return this.datas.features?.indexOf(feature) > -1;
	}

}

/** Contexte pour la création d'un task. */
export interface ITaskCreationCtx extends IShortDescCtx {
}

/** Spécialisation d'un ItemType de famille 'other'.
 *    Cet item peut être un simple fichier OU un folder (ancienne ressource devenue inconnue)
 **/
class ItemTypeOther extends ItemType {

	constructor(wspMeta: WspMetaUi, datas: IDatasItemType) {
		super(wspMeta, datas);
		this.reg.addToList("actions:item:burger", "download", 1, ExportRes.SINGLETON(), 101);
	}

	async getNewContent(reg: IReg<IWspUiEnv>, space: string | null, codeItem: string, options?: ONewContent): Promise<IBody | IFolder> {
		//Si des mimeTypes sont déclarés alors fichier vide, sinon c'est un dossier vide.
		return this.datas.resTypes ? "" : {name: codeItem, entries: []};
	}

	getSrcMainArea(ctx: ISrcMainCtx): SrcMainArea {return ctx.shortDesc?.srcSt === ESrcSt.folder ? defaultItemMainAreaFolder : defaultItemMainAreaRes}

	getMainStreamSrcUri(shortDesc: JSrcFields) {
		if (!shortDesc) return null;
		if (ITEM.getUriResType(shortDesc) === ITEM.EUriResType.root && shortDesc.srcSt === ESrcSt.folder) {
			// probabilité forte que ce soit une ancienne ressource aujourd'hui d'un type inconnu
			return SRC.addLeafToUri(shortDesc.srcUri, SRC.extractLeafFromUri(shortDesc.srcUri));
		}
		return shortDesc.srcUri;
	}

	getMainXmlSrcUri(shortDesc: JSrcFields): string | null {
		if (!shortDesc) return null;
		if (ITEM.getUriResType(shortDesc) === ITEM.EUriResType.root && shortDesc.srcSt === ESrcSt.folder) {
			// probabilité forte que ce soit une ancienne ressource aujourd'hui d'un type inconnu
			return SRC.addLeafToUri(shortDesc.srcUri, ItemTypeRes.PATH_META);
		}
		return shortDesc.srcUri;
	}

}

/** Spécialisation d'un ItemType de famille 'undef'
 * Cet item peut être un simple fichier OU un folder*/
class ItemTypeUndef extends ItemTypeOther {
	matchType(type?: string, fileName?: string): number {
		if (!this.datas.resTypes) return 1; //Toutes les extensions
		if (!type && !fileName) return 0;
		// Ext
		let matchExt = this.matchExt(fileName);
		if (matchExt != 0)
			return matchExt;
		// Type
		for (const resType of this.datas.resTypes) {
			if (resType.ct) for (const t of resType.ct) if (t === type) return 1;
		}
		return -1;
	}

	matchExt(fileName?: string): number {
		if (!fileName) return 0;
		const exts = this.getExtensions();
		if (exts == null) return 1;
		if (Array.isArray(exts)) return exts.find((entry) => SRC.matchExt(fileName, entry)) ? 1 : -1;
		return SRC.matchExt(fileName, exts) ? 1 : -1;
	}
}

/** Type d'item associé à une uri nulle. */
class ItemTypeNull extends ItemType {
	constructor(wspType: WspMetaUi) {
		super(wspType, {
			wspResUrl: wspType.wspTypeUrl,
			model: "sc_null",
			title: "Item non renseigné",
			iconsPng: wspType.chain.config.skinUrl.url + "wsp/itemType/icons/itemNull/"
		})
	}

	getIcon(shortDesc?: JSrcFields): string {
		return super.getIcon(null);
	}

	getIconOpen(shortDesc?: JSrcFields): string {
		return super.getIconOpen(null);
	}
}

/** Type d'item associé à une uri non null mais ne correspondant à aucun item. */
class ItemTypeNotFound extends ItemType {
	constructor(wspType: WspMetaUi) {
		super(wspType, {
			wspResUrl: wspType.wspTypeUrl,
			model: "sc_notFound",
			title: "Item non trouvé",
			iconsPng: wspType.chain.config.skinUrl.url + "wsp/itemType/icons/itemNotFound/"
		})
	}

	getIcon(shortDesc?: JSrcFields): string {
		return super.getIcon(null);
	}

	getIconOpen(shortDesc?: JSrcFields): string {
		return super.getIconOpen(null);
	}
}

/** Type d'item associé à une uri existante non reconnue par le WspType. */
class ItemTypeFallback extends ItemTypeOther {
	constructor(wspType: WspMetaUi) {
		super(wspType, {
			wspResUrl: wspType.wspTypeUrl,
			model: "sc_undef",
			title: "Item non reconnu"
		})
	}

	getIcon(shortDesc?: JSrcFields): string {
		if (shortDesc) switch (ITEM.getUriResType(shortDesc)) {
		case ITEM.EUriResType.folder :
			return this.datas.iconResFolder || this.wspMeta.getDefaultResFolderIcon();
		case ITEM.EUriResType.file :
			return this.datas.iconResFile || this.wspMeta.getDefaultResFileIcon();
		default:
			if (shortDesc.srcSt === ESrcSt.folder) return this.wspMeta.chain.config.skinUrl.url + "wsp/itemType/icons/itemUndefFolder/icovalid.png"
		}
		return this.wspMeta.chain.config.skinUrl.url + "wsp/itemType/icons/itemUndef/icovalid.png"
	}

	getIconOpen(shortDesc?: JSrcFields): string {
		if (shortDesc) switch (ITEM.getUriResType(shortDesc)) {
		case ITEM.EUriResType.folder  :
			return this.datas.iconResFolderOpen || this.wspMeta.getDefaultResFolderOpenIcon();
		case ITEM.EUriResType.file:
			return this.datas.iconResFile || this.wspMeta.getDefaultResFileIcon();
		}
		return this.wspMeta.chain.config.skinUrl.url + "wsp/itemType/icons/itemUndefFolderOpen/icovalid.png"
	}
}

/**
 * Librairies utilitaires pour la config.
 * Evite des chargements async inutiles dans les wspType.js et wspOption.js
 */
export const UTILS = {
	LIT: htmlLit,
	xhtml,
	generators,
	actions,
	loadFreeLib
};

/**
 * Initialise ce modèle documentaire ou cette extension de modèle.
 * Le module js issu du wsppack doit exporter une fonction nommée 'configWspType'.
 *
 * A minima, peuple le wspType.reg list 'itemTypes' d'objets IDatasItemType, le codeEntry de la liste étant le code du modèle.
 * TODO A spécifier :
 * - critères de recherches
 * - ...
 * Pour savoir ce qui doit vraiment être peuplé, des prefs du registre pourront être exploités.
 *
 * @return En général retourne null, voir Promise<void>.
 * Dans le cas d'extensions qui a besoin d'attendre qu'une autre extension soit initialisée,
 * une fonction configWspMeta peut être retournée et sera executée aprrès que toutes les
 * extensions soient chargées.
 */
interface IWspTypeModule {
	configWspMeta(wspMeta: WspMetaUi, wspResUrl: IEndPoint, utils: typeof UTILS): null | Promise<void | postConfigWspMeta>;
}

type postConfigWspMeta = (wspMeta: WspMetaUi, utils: typeof UTILS) => null | Promise<void | postConfigWspMeta>

/** Fournisseur d'un système de lifeCycle : WspMetaUi pour les items et ItemTypeTask pour les tâches. */
export interface ILcProvider {
	getLcName(): string

	getLcStates(): Map<string, IDatasLcState> | null

	getLcState(code: string): IDatasLcState | null

	getLcStateOrUnknown(code: string): IDatasLcState | null

	getLcTransitions(): Map<string, IDatasLcSimpleTransition> | null

	getLcTransition(code: string): IDatasLcSimpleTransition | null

	getLcTransitionsGroupOrder(): string | null
}


/** Modèle d'item issu d'un IWspPackModule, obtenu via wspType.reg.getList<IDatasItemType>('itemTypes'). */
export interface IDatasItemType {
	model: string
	title: string
	family?: EItemTypeFamily
	sgn?: string
	desc?: string
	helpId?: helpId

	/** Hierarchie des dossisers parent pour afficher ce type d'item. */
	classification?: IDatasDirItemType[]

	/** Ordre dans le dossier.*/
	sortKey?: string

	/** URL Schema XML pour les itemTypes de famille XML ou res (metas). */
	schema?: string

	/** Editors disponibles pour cet itemType. */
	editors?: Dict<IDatasEditor>

	/** Impose un mode readOnly, indépendamment de la gestion des roles (pour compat 4.2, à suppr en SC5.1?) */
	readOnly?: boolean

	/** Impose le fait que cet item n'apparaisse pas dans les fenêtres de création d'item */
	noCreator?: boolean

	/** Permissions de création de l'item */
	permsCreator?: string[]

	/** Template pour la construction d'un name (ie 1ère partie du code de l'item avant l'extension) d'un item en création. */
	itemNameTpl?: string

	/**
	 * Extension des items pour family 'xml' et 'folder'.
	 * Extension du code de l'item ou liste d'extensions autorisées pour ce type d'items.
	 * Ex: ".folder" ou [".folder", ".dossier"]
	 */
	itemExt?: string | string[]

	/**
	 * Extensions et Content-types des items pour family==='res'
	 * Si non renseigné, signifie que l'itemType accepte *toutes* les extensions (qui valident resExtsMatcher si défini)
	 */
	resTypes?: {
		/**
		 * Extensions possibles du code de l'item compatibles avec les content-types définis.
		 * Ex: [".png"] ou [".jpg", ".jpeg"]
		 */
		ext: string[]
		/** Content-types possibles pour ces extensions. */
		ct: string[]
	}[]

	/**
	 * Rêgle de validation des extensions pour family==='res'
	 * Remarque : n'a de sens que si resTypes n'est pas renseigné
	 */
	resExtsMatcher?: RegExp,

	/** Liste des responsabilités de l'item.*/
	resps?: IDatasResp[]

	/** URL d'un contenu vide pour family==='res'. La key est l'extension. Ex: {"ods": ".../empty.ods"}
	 *    la clef "*" est utilisée par défaut, si aucun emptyUrl est défini explicitement pour l'extension
	 * */
	resEmptyUrl?: Dict<string>

	/** URL du dossier d'icones png (doit se terminer par un '/'). */
	iconsPng?: string

	/** URL du dossier d'icones png (état ouvert) pour les itemTypes de famille folder (doit se terminer par un '/'). */
	iconsPngOpen?: string

	/** URL de l'icone de dossier interne à un item de famille 'folder'. */
	iconResFolder?: string

	/** URL de l'icone de dossier interne à un item de famille 'folder' (état ouvert). */
	iconResFolderOpen?: string

	/** URL de l'icone de fichier interne à un item de famille 'folder'. */
	iconResFile?: string

	/** Config de la vue principale des items (hors tasks) dans le contexte de l'atelier. */
	mainView?: IDataSrcMainView

	/**
	 * Config de la vue restreinte en lecture et simplifiée à l'item lui-même. Destinée à être utilisée
	 * pour visualiser le contenu de l'item, sans fonction d'écriture et sans intégration avec le reste
	 * du réseau des items (ie pas de dyn-gen qui abstrait la fragmentation par exemple). Usages prévus:
	 * - vue d'une entrée d'historique,
	 * - vue de l'item surchargé dans le contexte d'un item dans un atelier calque ou dérivé,
	 * - vue de la source d'un item clone et proxy
	 */
	isolatedView?: IDatasSrcView

	/** Config de mainViews supplémentaires exploitées dans des contextes particuliers (contribution externe...) */
	otherMainViews?: Dict<IDataSrcMainView>

	/**
	 * Config de la prévisualisation de l'item utilisée au survol d'une référence à cet item.
	 * A n'exploiter que lorsque l'item se prête à un vision synthétique image, résumé...
	 */
	previewMini?: {
		/** Tag à utiliser pour intégrer la preview. */
		tag: 'img' | 'video' | 'audio' | 'iframe' | 'wsp-dyngen-preview' | 'wsp-remote-view' | 'wsp-code-view'
		/** Paramètres de transformation du flux principal de l'item (ex: "transform=img2img&..."). */
		transform?: string
		/** Pour le tag video, possibilité de spécifier les paramètres de transformation pour obtenir un poster (ex: "transform=img2img&..."). */
		poster?: string
		/** Pour les tags iframe,  et wsp-dyngen-preview, valeurs css 'width' et 'height' optimales de la preview. */
		w?: string
		h?: string
		/** Pour wsp-dyngen-preview : code du dynGen */
		codeGen?: string
		/** Pour wsp-dyngen-preview : (skin...) */
		params?: Jsonisable
		/** Pour wsp-remote-view : paramètres d'init. */
		init?: Dict<any>
	}

	/**
	 * Wizards de création de tasks (et d'items 'complexes' ? ).
	 * Une même tâche / item peut proposer plusieurs wizards : la définition de la fonction customSelector
	 * est alors nécessaire pour fournir des libellés différents.
	 */
	createViews?: {
		/** Permission à valider pour utiliser ce wizard de création. */
		perm?: string
		/** Contraintes de sélection supplémentaires et/ou personnalisation du libéllé d'accès à ce wizard. Retourne le libellé à afficher. */
		customSelector?: (reg: IReg<IWspUiEnv>, options?: any) => false | string
		/** Définition de la vue. */
		viewDef: IDatasSrcView
	}[]

	/** Nom de l'attribut racine du document dans lequel injecter un ctId issu du svc de l'atelier. */
	createWithCtIdInAtt?: string

	//Projet d'ajout de conversions sélectionnable dans l'itemCreator.
	//createActions?: (IDatasItemConvertor | IAction<IRegPointer<IItemUiEnv>>)[]

	/** Arbre des générateurs. */
	generators?: (IDatasItemGenNode | IDatasItemCidNode)[]

	/** Indique que ce type d'item peut-être vu comme un document. */
	asDoc?: {
		/** Liste des types d'items constituant des "supports" (ie racines de publications) pour ce "document". */
		supports?: {
			/** Modèle du support. */
			itModel: string
			/** Clé de l'éditeur à utiliser pour éditer ce support (masquant le lien vers le document). */
			keyEditor: string
			/** Constructeur d'un nouveau contenu pour ce support intégrant un lien vers le document dont le shortDesc est passée dans ONewContent.docShortDesc. */
			newContentBuilder: (wsp: Wsp, space: null | string, codeItem: string, options?: ONewContent) => Promise<IBody | null>
		}[]
	}

	/**
	 * Méthode optionnelle de configuration "free" en surchargeant les comportements par défaut de ItemType.
	 *
	 * L'impl peut s'appuyer sur des propriétés de itemType.reg par exemple pour connaitre
	 * le contexte de déploiemnt, mais ne doit pas s'appuyer sur les caractéristiques
	 * ou permissons du user courant (propriétés dynamiques).
	 *
	 * Peut retourner un autre objet ItemType que celui passé en paramètre.
	 */
	configItemType?(itemType: ItemType): ItemType

	/** EndPoint du wspRes à l'origine de ce module. */
	wspResUrl?: IEndPoint
}

/** Item type des tasks */
export interface IDatasTaskItemType extends IDatasItemType {

	/** URL Schema XML du champ description de la task */
	descriptionSchema?: string

	/** URL Schema XML d'un champ commentaire */
	commentSchema?: string

	/**
	 * Vues de consultation / édition des tasks.
	 * Liste ordonnée : la première vue qui matche les critères de sélection (ifInvolvement, ifLifeCycleState et customSelector)
	 * est utilisée.
	 */
	taskViews?: IDatasTaskView[]

	/** Intitulé de ce LC */
	lcName?: string;

	/** Liste des states de cette task */
	lcStates: IDatasLcState[]

	/** Liste des transitions entre les states de cette task */
	lcTrans: IDatasLcSimpleTransition[]

	/** Ordonnancement des groupes des transitions de cette task */
	lcTransGroupOrder?: string

	/** Liste des transitison à la création d'une task. */
	createTrans: IDatasLcSimpleTransition[]

	/** Pré-remplissage de la task à la création. */
	onCreate?: (
		{ addUserToResp: string } |
		{ detailsTpl: string } |
		((ctx: ITaskCreationCtx, taskType: ItemTypeTask, states: JTaskStates) => void)
		)[]

	/** Liste des responsabilités de la tâche.*/
	resps: IDatasResp[]

	/** Features actives sur cette task */
	features?: ETaskFeatures[]
}

export enum ETaskFeatures {
	deadline = 'deadline',
	scheduling = 'scheduling'
}


/** Cycles de vie d'un item ou d'une task. */
export interface IDatasLcState {
	code: string
	name: string
	icon?: string
	iconUrl?: string // URL résolue dynamiquement à partir de 'icon'
	color?: string
	taskStage?: ETaskStage // Pour les Lc des tasks uniquement.
}

/** Cycle de vie : default state - code
 * @see  com.scenari.src.search.exp.LifeCycleStates.DEFAULT_STATE */
export const LC_STATE_DEFAULT_KEY = "#default";

/** Cycle de vie : default state - name (si non spécifié dans le modèle)
 * @see  com.scenari.src.search.exp.LifeCycleStates.DEFAULT_STATE */
export const LC_STATE_DEFAULT_NAME = "Non défini";

/** Cycle de vie : default name */
export const LC_DEFAULT_NAME = "Cycle de vie";

export interface IDatasLcSimpleTransition {
	code: string
	targetState: string
	restrictFromStates?: string[]
	restrictForItemTypes?: string[] // Pour les transitions des items uniquement (hors tasks).
	name: string
	icon?: string
	iconUrl?: string // URL résolue dynamiquement à partir de 'icon'
	sortKey?: string
	group?: string
	showPerm?: string
	enablePerm?: string
	isTransitionVisible?: (shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>, emitter: HTMLElement) => boolean
	isTransitionEnabled?: (shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>, emitter: HTMLElement) => boolean
	paramsProvider?: ILcTransitionParamsProvider
	/* Contrôle libre avant l'exécution, pouvant renvoyer des messages UI */
	controlBeforeExecute?: (shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>, emitter: HTMLElement, transitionParams?: { [srcUri: string]: { [key: string]: Jsonisable } }) => Promise<boolean>
}

export interface ILcTransitionParamsProvider {
	/** @return false si le processus de construction a été annulé ou est en erreur, true pour poursuivre l'exécution de la transition
	 * @param params : liste des paramètres pour chaque srcUri, ou '*' pour toutes */
	computeParams: (params: { [srcUri: string]: { [key: string]: Jsonisable } }, shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>, emitter: HTMLElement) => Promise<boolean>
	rollbackTransition?: (params: { [srcUri: string]: { [key: string]: Jsonisable } }, shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>) => Promise<void>
}

export interface IDatasResp {
	code: string
	name: string
	desc?: string
	userCard: 'single' | 'multi'
	usersSelection?: {
		userType: 'users' | 'groups' | 'all'
		restrictFromGroups: string[] //Opérateur OR
	}
	required?: boolean | RegExp //regexp sur le state de la task
	//Pour les tasks uniquement.
	involvementRules?: {
		addInv: EInvolvement
		ifState?: RegExp
	}[]
}


/** */
export interface IDatasEditor {
	/** Key du wed pour la zone principale d'édition. */
	mainWed: string

	/** Key du wed pour l'outline. */
	outlineWed?: string

	/** Key du wed pour la altview. */
	previewWed?: string

	/**
	 * Si help est renseigné, mais pas help.helpDbProv, ce dernier sera automatiquement renseigné
	 * avec celui du wspMeta (qui devrait alors être configuré).
	 */
	help?: OHelpViewsInit

	/** TODO Surcharge des perms pour configurer les restrictions en écriture. */
	//writePerms?: string | string[]

	/**
	 * Configure le registre appliqué à cet éditor. Si la méthode existe, cela implique
	 * que le contexte appelant DOIT créer un SubReg dédié à cet éditor avant d'appeler
	 * cette méthode.
	 */
	initReg?: (reg: IReg<IItemUiEnv>, editor: IWedEditor) => Promise<void>

	/** Surcharge les configs de l'éditor. */
	overrideConfigs?: (editorConfig: OWedEditorBoxConfig, wedMgrConfig: OWedManagerConfig, editor: IWedEditor) => Promise<void>
}

export interface IDataSrcMainView {
	/** Clé de l'éditeur xml. Pour les items de la famille "xml" et "res" (metas). */
	xmlEditor?: string
	/** Config de la view affichant le contenu. Pour les items de la famille "res" et "folder". */
	resView?: IDatasSrcView
	/** Onglets secondaires. Pour tous les types d'items. */
	subTabs?: IDatasItemTab[]
	/** Libellé de l'onglet principal. */
	mainTabLabel?: string
	/** Icon de l'onglet principal. */
	mainTabIcon?: string
	/** Ajoute un bandeau des erreurs issues des SS. */
	showSsErrors?: {
		errors: { code: RegExp }[]
	}
	/** Actions de conversion / transformation de cet item. */
	convertors?: (IDatasItemConvertor | IAction<IRegPointer<IItemUiEnv>>)[]
	/** Surcharge du reg à l'init de la mainView. */
	configReg?: (reg: IReg<IItemUiEnv>) => void
}

export type IDatasItemConvertor = {
	label: string
	perm?: string
	/* Contrôle libre avant l'exécution, pouvant renvoyer des messages UI */
	controlBeforeExecute?: (ctx: IRegPointer<IItemUiEnv>, ev?: Event) => Promise<boolean>
	executor: { algo: 'switch', tagName: string, tagNs: string, cleanupSchemaItModel?: string }
		| { algo: 'xsl', xslPath?: string, xsl: string | XSLTProcessor, cleanupSchemaItModel?: string, xslParams: { name: string, value: string }[] }
}

export interface IDatasItemTab {
	code: string //identité pour last-datas notamment.
	label: string
	description?: string
	icon?: string
	showPerms?: string | string[]
	view: IDatasSrcView
}

/** Config d'une view de l'item. */
export interface IDatasSrcView {
	/** Obligatoire: Nom du tag de la view. */
	tag: "wsp-item-img" | "wsp-item-video" | "wsp-item-audio" | "wsp-item-iframe" | "wsp-item-txt" | "wsp-item-code-ed" | "wsp-item-xmled" | "wsp-item-dyngen" | "wsp-item-remote"
		| "wsp-task-layout" | "wsp-task-field" | "wsp-task-xmlfield" | "wsp-task-dyngen" | "wsp-task-ct-edit" | "wsp-task-ct-dyngen"
		| "c-free" | "c-free-async" | any

	/** Attributs statiques affectés au tag. */
	atts?: Dict<string>

	/** Config du tag. */
	init?: OItemImgInit | OItemVideoInit | OItemAudioInit | OItemIframeInit | OItemTxtInit | OItemCodeEdInit | OItemXmlEdInit | OItemDynGenInit | OItemRemoteInit
		| OTaskLayoutInit | OTaskFieldInit | OTaskXmlFieldInit | OTaskDynGenInit | OTaskCtEditInit | OTaskCtDynGenInit
		| OFreeElementInit | OFreeElementAsyncInit | any

	/**
	 * View free : url du module à charger définissant 'tag'.
	 * Si le Module implémente IFreeLibSrcView.initFreeLib(), cette méthoe est appelée une seule fois
	 * après son chargement.
	 * L'alias :wspRes: peut être utilisé pour pointer une ressource du pack courant SI l'itemType appartient au pack courant.
	 * ATTENTION : Si on est en extension qui surcharge un ItemType d'un autre pack, l'URL doit être résolue
	 * en amont à l'injection : `{tag: "c-free-async", freeLib: wspResUrl.resolvePath(":wspRes:...")}`
	 */
	freeLib?: string
}

/** @see IDatasSrcView.freeLib */
export interface IFreeLibSrcView extends Dict<any> {

	/** Appelé une et une seule fois. Permet de lier le code free au code de dev-core (via reg.env.resolver). */
	initFreeLib?(reg: IReg<IChainEnv>): Promise<void>

	/** Instanciation custom de l'objet d'init pour le srcView.tag qui est généralement "c-free" ou "c-free-async". */
	newFreeSrcViewInit?(srcView: IDatasSrcView, reg: IReg<IItemUiEnv>): OFreeElementInit | OFreeElementAsyncInit | any
}

/**
 * Charge une lib et garanti l'appel une ET UNE SEULE fois à initFreeLib(reg).
 */
export async function loadFreeLib(lib: IEndPoint, reg: IReg<IChainEnv>): Promise<IFreeLibSrcView> {
	return lib.importJs().then(async (module: IFreeLibSrcView) => {
		if (module.initFreeLib && !(module.initFreeLib as any).called) {
			await module.initFreeLib(reg);
			(module.initFreeLib as any).called = true;
		}
		return module;
	})
}

/** Structuration complexe de IDatasSrcView */
export interface IDatasTagView extends IDatasSrcView {
	children?: (IDatasTagView | string)[];
}

/** Config d'une view d'une tâche. */
export interface IDatasTaskView {
	/** Vue accessible aux users qui ont au moins une des implications listées sur cette tâche. */
	ifInvolvement?: EInvolvement | EInvolvement
	/** Vue accessible si la tâche est dans un états listés. */
	ifLifeCycleState?: RegExp
	/** Vue accessible si elle dans une app dont le localName du tag match cette regexp. */
	ifInApp?: RegExp
	/** Vue accessible si la perm spécifiée est autorisée */
	ifPerm?: string
	/** Filtre custom supplémentaire lié au contexte d'appel. */
	customSelector?: (reg: IReg<IItemUiEnv>, options?: any) => boolean
	/** Définition de la vue. */
	viewDef: IDatasSrcView
}


/** Structure de donnée d'un dossier de classification des itemTypes. */
export interface IDatasDirItemType {
	code: string
	sortKey: string
	label: string
	closed: boolean
	helpId?: helpId
}

export interface IDirItemType extends IDatasDirItemType {
	children: (IDirItemType | ItemType)[]
}

export function isDirItemType(d: any): d is IDirItemType {return d && Array.isArray(d.children)}

/**
 * Définition d'un noeud dans l'arbre des publications.
 * Propriétés communes aux dir, gen, subGen et cid.
 */
export interface IDatasItemPubNode {
	/** Type de noeud. */
	genNodeType: 'dir' | 'gen' | 'subGen' | 'cid'
	/** Code du dossier (dir) ou identification du gen_subGen_deploy dans sa stack (code unique dans un niveau de l'arbre). */
	codeGenStack: string
	/** Libélé court du génrateur afiché dans l'arbre. */
	label: string
	/** Icone custom liée au gen (ie indépendant du skin). Par défaut : icone en fonction de genNature. */
	icon?: string //path de type :wspRes:...
	iconUrl?: string //url résolue dynamiquement à partir de 'icon'
	/** Visibilité du noeud. */
	perms?: string | string[]
	/** Noeuds fils. */
	ch?: IDatasItemPubNode[]
	/** Codes de listes définissant des actions (en plus des listes par défaut) */
	actionListsCode?: string[]
}

/** Propriétés spécifiques aux générateurs et subGen. */
export interface IDatasItemGenNode extends IDatasItemPubNode {
	/** Nature du générateur. */
	genNature?: 'web' | 'pdf' | 'odt' | 'xml' | 'pack' | 'deploy' | 'other'
	/** Surcharge du titre du générateur (JGenInfo.title). */
	genTitle?: string
	/** Skin class de ce générateur */
	genSkinClass?: string
	/** Image d'illustration */
	illus?: string //path de type :wspRes:...
	illusUrl?: string //url résolue dynamiquement à partir de 'illus'
	/** Template xhtml des propriétés du générateur */
	props?: (ctx: IPubCtx) => Promise<TemplateResult>
	needExtraProps?: boolean
	showRefreshBtn?: boolean
	/** Surcharge de la zone d'infos. */
	infos?: (ctx: IPubCtx) => Promise<TemplateResult>
}

/** Propriétés spécifiques aux déploiements CID. */
export interface IDatasItemCidNode extends IDatasItemPubNode {
	/** Restriction des serveurs cid possibles fournit par le serveur via un is.
	 * si cidHostIs === null on ne retient aucun des serveurs fournis */
	cidHostIs?: string[][]

	/** Ajoute un champ libre de saisie de l'URL du manifest CID. */
	cidManifestUrlInput?: boolean

	/** Alternative : imposition d'un manifest précis (independamment de ceux fournis par le serveur).*/
	//manifestUrl?: string A voir si usage...

	/** Restriction des process possibles via une fonction. */
	cidProcessSelector?: (ctx: IPubCtx) => Promise<CidProcess[]>

	/** Si cidProcessSelector null, restriction des process possibles via un is. */
	cidProcessIs?: string[]

}

/** Contexte pour les actions et les propriétés des générateurs. */
export interface IPubCtx {
	reg: IReg<IItemUiEnv>
	uiContext: HTMLElement
	wsp: Wsp
	shortDesc: JSrcFields
	pubNode: IItemPubNode

	/** Pour les générateurs. */
	genInfo?: JGenInfo

	/** destination fixée de la génératon. Usage styler */
	customDestPath?: wspUri

	/**
	 * Pour les générateurs.
	 * Construit les props pour lancer une génération.
	 * ATTENTION doit retourner une nouvelle instance, pas une constante car peut être surchargé.
	 * @return false : propriétés invalides, la génération doit être annulée.
	 */
	genProps(): Promise<Jsonisable | null | false>

	/** Pour un déploiement CID. */
	cidInfo?: ICidInfos
}


/** Noeud de l'arbre des générateurs, avec ses noeuds fils. */
export interface IItemPubNode extends IDatasItemGenNode, IDatasItemCidNode {
	//genSt?: string//todo... issu du serveur
	ch?: IItemPubNode[]
}


/** Définition des fichiers WED. */
type IWedDef = {
	path: string
}

export interface IItemTypePointer {
	itemType: ItemType
}

/** Un environnement d'itemType est issu d'un env d'un Wsp et l'itemType défini. */
export type IItemTypeEnv = IWspEnv & IItemTypePointer;

export type IItemTypeUiEnv = IWspUiEnv & IItemTypeEnv;

/** Options de construction du nouveau contenu.  */
export interface ONewContent {
	/** Pour wspDocApp : création d'un item de type .support avec une ref à son contenu principal. */
	docShortDesc?: JSrcFields

	/** Pour les RemotePrims : url cible. */
	remoteUrl?: string
}

/**
 * Factory de la vue principale d'un item ou d'une task (voire d'un space ?).
 *
 * Idée générale : le contexte de cette factory initialisé en amont par le container (ItemViewer) intègre
 * déjà le reg<IItemTypeEnv & IUiEnv> et le shortDesc (voir le longDesc si dispo) de l'item.
 *
 * Avantages :
 * - Le reg propre au contexte graphique et à cet itemType peut être mutualisé pour différents items de l'ItemViewer.
 * - Cet ItemMainArea peut retourner les icones / labels / descriptions et surtout les perms spécifiques à l'item.
 * - L'implémentation du widget ItemMain qui instancie ses propres WspLivePlace et Reg<IItemEnv> ne s'occupe que de
 *   rafraichir son longDesc, considérant l'itemType et le reg parfaitement stables (ie, si l'itemType est changé,
 *   la view est renouvelée par le container).
 *
 * Contraintes : le container (ItemViewer) doit
 * - charger le shortDesc (ou mieux le longDeesc), associer le reg, etc en amont
 * - écouter via une WspLivePlace instanciée en 1er pour détecter les changements de ces items, rafraichir les représentations
 *   graphique des entêtes (tab), détecter un changement d'itemType et remplacer les views instanciées à la volée si nécessaire.
 *
 */
export class SrcMainArea extends Area<ISrcMainCtx, SrcMain> {

	skinMainView: string;

	skinOverMainView: string;

	constructor(public tagName: string, public titleAsLabel?: boolean) {
		super(tagName);
	}

	getLabel(ctx: ISrcMainCtx): string {
		if (typeof this._label === 'function') return this._label(ctx, this);
		return this.titleAsLabel ? ctx.shortDesc.itTi || SRC.extractLeafFromUri(ctx.shortDesc.srcUri) : SRC.extractLeafFromUri(ctx.shortDesc.srcUri);
	}

	getIcon(ctx: ISrcMainCtx): string {
		if (typeof this._icon === 'function') return this._icon(ctx, this);
		return ctx.reg.env.itemType.getIcon(ctx.shortDesc);
	}

	buildBody(ctx: ISrcMainCtx, lastDatas?: JLastDatas): SrcMain {
		return (document.createElement(this.tagName) as SrcMain).initialize({
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			skin: this.skinMainView,
			skinOver: this.skinOverMainView,
			lastDatasKey: this.getLastDatasKey(ctx),
			lastDatas
		});
	}
}

let genRng: Promise<Schema>;

function getGenericSchema(): Promise<Schema> {
	if (!genRng) genRng = getSchema(IO.asEndPoint("/@lib@/wsp/generic.rng"));
	return genRng;
}


async function getSchema(ep: IEndPoint): Promise<Schema> {
	if (!SKMETALIB) {
		const mdWsp = import ("back/wsp/widgets/wed/schemaMetaWsp.js");
		const mdSk = import ("lib/edit/schema/schemaBuilder.js");
		SKMETALIB = (await mdWsp).SKMETALIB_WSP;
		SchemaBuilder = (await mdSk).RelaxNgSchemaBuilder;
	}
	return new SchemaBuilder(SKMETALIB).buildRelaxNgFromUrl(ep);
}

let SKMETALIB: SkMetaLib; //lazy Loading
let SchemaBuilder: typeof RelaxNgSchemaBuilder; //lazy Loading

function hasTasks(types: Dict<IDatasItemType>): boolean {
	for (const md in types) {
		if (types[md]?.family === EItemTypeFamily.task) return true;
	}
	return false;
}

function hasResps(types: Dict<IDatasItemType>): boolean {
	for (const md in types) {
		const resps = types[md]?.resps;
		if (resps && resps.length) return true;
	}
	return false;
}

class ItemMainConvert extends actions.Action<IRegPointer<IItemUiEnv>> {

	constructor(public dataConvertor: IDatasItemConvertor) {
		super();
		this._label = dataConvertor.label;
		this.requireVisiblePerm(dataConvertor.perm);
	}

	async execute(ctx: IRegPointer<IItemUiEnv>, ev?: Event) {
		const mod = await import("back/wsp/views/itemMain.js");
		const exec = this.dataConvertor.executor;
		if (this.dataConvertor.controlBeforeExecute && !await this.dataConvertor.controlBeforeExecute(ctx, ev)) return;
		switch (exec.algo) {
		case "switch":
			return mod.convertBySwitchTag(ctx, exec);
		case "xsl":
			return mod.convertByXsl(ctx, exec);
		}
	}
}


const defaultItemMainAreaXml = new SrcMainArea('wsp-itemmain-xml');
const defaultItemMainAreaRes = new SrcMainArea('wsp-itemmain-res');
const defaultItemMainAreaFolder = new SrcMainArea('wsp-itemmain-folder');
const defaultTaskMainArea = new SrcMainArea('wsp-taskmain').setLabel((ctx: ISrcMainCtx) => ctx.shortDesc.actTi || ctx.reg.env.itemType.getTitle());
